import type { KpiCard } from '@oxxean/types';

/**
 * Formateo de presentación.
 *
 * Toda cifra se redondea SOLO aquí (deuda 4 de definicion-indicadores.md): el
 * backend entrega precisión completa y la interfaz decide cuántos decimales
 * muestra. Las fechas se formatean explícitamente en `America/Santiago`
 * (DEC-A10): usar la zona del navegador desplazaría los períodos.
 */
export const LOCALE = 'es-CL';
export const TIME_ZONE = 'America/Santiago';

export type ValueFormat = KpiCard['format'];

const integerFormatter = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function formatNumber(value: number, format: ValueFormat = 'integer'): string {
  switch (format) {
    case 'integer':
      return integerFormatter.format(value);
    case 'decimal':
      return decimalFormatter.format(value);
    case 'percent':
      // El sufijo va con espacio duro: es la convención tipográfica en español.
      return `${decimalFormatter.format(value)} %`;
  }
}

/** Variación entre períodos. Se muestra con signo explícito. */
export function formatDelta(delta: number): string {
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${decimalFormatter.format(Math.abs(delta))} %`;
}

/** Cobertura 0..1 → porcentaje entero. */
export function formatCoverage(coverage: number | null | undefined): string | null {
  if (coverage === null || coverage === undefined || Number.isNaN(coverage)) return null;
  return `${integerFormatter.format(Math.round(coverage * 100))} %`;
}

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Una FECHA DE CALENDARIO (`2026-07-15`), distinta de un INSTANTE.
 *
 * ── El defecto que corrige ─────────────────────────────────────────────────
 * Un caso registrado el 15 de julio aparecía en la tabla como **14-07-2026**.
 *
 * El backend envía las fechas de calendario como `YYYY-MM-DD` —son columnas `date`
 * en PostgreSQL, sin hora—, pero `new Date('2026-07-15')` las interpreta como
 * medianoche UTC. Convertir eso a `America/Santiago` resta cuatro horas y con
 * ellas un día: el 15 pasaba a ser el 14.
 *
 * Convertir de zona una fecha de calendario NO tiene sentido: el 15 de julio es el
 * 15 de julio en Santiago, en Madrid y en Tokio. La conversión sólo corresponde a
 * los instantes (`createdAt`, `lastLoginAt`), que sí ocurren en un momento único.
 *
 * Era peor que un error de presentación. `toDateInputValue` desplazaba igual, así
 * que el formulario de edición nacía con el 14 y al guardar almacenaba el 14:
 * cada edición retrocedía la fecha un día más, en silencio y sin límite.
 */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDate(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;

  const calendar = CALENDAR_DATE.exec(iso);
  if (calendar) {
    const [, year, month, day] = calendar;
    return `${day}-${month}-${year}`;
  }

  const date = toDate(iso);
  return date ? dateFormatter.format(date) : fallback;
}

export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  // Un instante SÍ se convierte: ocurrió en un momento único y se lee en Chile.
  const date = toDate(iso);
  return date ? dateTimeFormatter.format(date) : fallback;
}

/** Convierte un ISO a `yyyy-MM-dd` para `<input type="date">`. */
export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';

  // Ya es una fecha de calendario en el formato que el control espera: devolverla
  // tal cual. Pasarla por una conversión de zona la desplazaría un día.
  if (CALENDAR_DATE.test(iso)) return iso;

  const date = toDate(iso);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Formatea un RUT normalizado para mostrarlo: `272933448` → `27.293.344-8`.
 *
 * ── Por qué está aquí y no se importa de `@oxxean/shared` ──────────────────
 * Ese paquete ya tiene `formatRut`, y reutilizarlo parecía lo correcto. No lo es:
 * el frontend NO depende de `@oxxean/shared` —no lo importa en ningún archivo— y
 * `vite.config.ts` solo declara alias para `@oxxean/types`. Importarlo compila
 * —`tsc` resuelve por node_modules— y deja la aplicación EN BLANCO en el navegador,
 * porque Vite no puede resolver el módulo en desarrollo. El fallo no aparece en la
 * compilación ni en el linter: solo al abrir la página.
 *
 * Añadir el paquete completo como dependencia del frontend por un formateador de
 * seis líneas es más costo del que ahorra. Si algún día el frontend necesita
 * `safeDivide` u otra pieza de cálculo, entonces sí corresponde declarar el alias y
 * la dependencia, y esta función se reemplaza por la importación.
 *
 * Devuelve `null` si no parece un RUT, para que quien llama decida qué mostrar: en
 * una pantalla de conciliación, que el origen envíe algo que no es un RUT es en sí
 * mismo el hallazgo y no debe ocultarse.
 */
export function formatRut(input: string | null | undefined): string | null {
  if (!input) return null;

  const limpio = input.replace(/[.\-\s]/g, '').toUpperCase();
  // Cuerpo de 6 a 9 dígitos más dígito verificador (0-9 o K).
  if (!/^\d{6,9}[0-9K]$/.test(limpio)) return null;

  const cuerpo = limpio.slice(0, -1);
  const verificador = limpio.slice(-1);
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${conPuntos}-${verificador}`;
}

/**
 * Hoy en Chile, como `yyyy-MM-dd`.
 *
 * `new Date().toISOString().slice(0, 10)` da el día en UTC: entre las 20:00 y la
 * medianoche en Santiago ya es el día siguiente allí, y un archivo descargado el
 * lunes por la tarde se nombraría con la fecha del martes.
 */
export function todayInChile(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return '—';
  if (ms < 1000) return `${integerFormatter.format(ms)} ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes} min ${seconds.toString().padStart(2, '0')} s`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${(minutes % 60).toString().padStart(2, '0')} min`;
}

export const MONTHS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

export function monthLabel(month: number | undefined): string {
  return MONTHS.find((m) => m.value === month)?.label ?? '—';
}
